import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import SafeIcon from '../../components/common/SafeIcon';
import BrandVideoPlayer from '../../components/training/BrandVideoPlayer';
import ReelCropSettings from '../../components/training/ReelCropSettings';
import ReelLifecycleActions from '../../components/training/ReelLifecycleActions';
import { useAuth } from '../../context/AuthContext';
import {
  checkCanEditReel,
  checkIsAdminOrHigher,
  fetchReelById,
  getVideoMetadataFromUrl,
  updateReelCrop,
  updateReelMetadata,
} from '../../services/training';
import { DEFAULT_CROP_SETTINGS, isVerticalSource, resolveReelDisplay } from '../../utils/reelCrop';

export default function ReelEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();

  const [reel, setReel] = useState(null);
  const [caption, setCaption] = useState('');
  const [aspectRatio, setAspectRatio] = useState(null);
  const [cropSettings, setCropSettings] = useState(DEFAULT_CROP_SETTINGS);
  const [canEdit, setCanEdit] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');
      try {
        const row = await fetchReelById(id);
        if (cancelled) return;
        if (!row) {
          setError('Reel no encontrado');
          setReel(null);
          return;
        }
        setReel(row);
        setCaption(row.caption || row.title || '');
        if (row.isDraft) {
          navigate(`/app/reels/${row.id}/borrador`, { replace: true });
          return;
        }
        setCropSettings({
          cropMode: row.cropMode ?? DEFAULT_CROP_SETTINGS.cropMode,
          focalX: row.focalX,
          focalY: row.focalY,
        });

        const [allowed, admin] = await Promise.all([
          row.uploadedBy ? checkCanEditReel(row.uploadedBy) : Promise.resolve(false),
          checkIsAdminOrHigher(),
        ]);
        if (!cancelled) {
          setCanEdit(allowed);
          setIsAdmin(admin);
        }

        let ratio = row.sourceAspectRatio;
        if (!ratio && row.videoUrl) {
          try {
            const meta = await getVideoMetadataFromUrl(row.videoUrl);
            ratio = meta.aspectRatio;
          } catch {
            // keep null — wide-source fallback in ReelCropSettings
          }
        }
        if (!cancelled) setAspectRatio(ratio);
      } catch (err) {
        if (!cancelled) setError(err.message || 'No se pudo cargar el reel');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [id]);

  const previewDisplay = useMemo(
    () => resolveReelDisplay({
      sourceAspectRatio: aspectRatio ?? reel?.sourceAspectRatio,
      cropMode: cropSettings.cropMode,
      focalX: cropSettings.focalX,
      focalY: cropSettings.focalY,
    }),
    [aspectRatio, reel?.sourceAspectRatio, cropSettings]
  );

  const handleSave = async (e) => {
    e.preventDefault();
    if (!reel || !canEdit) return;
    setSaving(true);
    setError('');
    try {
      await updateReelMetadata(reel.id, { caption });
      const updated = await updateReelCrop(reel.id, {
        sourceAspectRatio: aspectRatio ?? reel.sourceAspectRatio,
        ...cropSettings,
      });
      setReel(updated);
      if (reel.isArchived) {
        navigate('/app/entrenamientos/archivo', { replace: true });
      } else {
        navigate('/app', { replace: true, state: { uploadSuccess: 'edit' } });
      }
    } catch (err) {
      setError(err.message || 'No se pudieron guardar los cambios');
    } finally {
      setSaving(false);
    }
  };

  const handleReelUpdated = async () => {
    const row = await fetchReelById(id);
    if (row) setReel(row);
  };

  if (authLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center text-[var(--text-secondary)] text-sm font-bold uppercase tracking-widest">
        Cargando…
      </div>
    );
  }

  if (!loading && reel && !canEdit) {
    return <Navigate to="/app" replace />;
  }

  const isVertical = isVerticalSource(aspectRatio ?? reel?.sourceAspectRatio);
  const hasClip = reel?.clipStartSeconds != null && reel?.clipEndSeconds != null;

  return (
    <div className="max-w-3xl mx-auto px-6 py-10 pb-28">
      <div className="flex items-center gap-4 mb-8">
        <Link
          to={reel?.isArchived ? '/app/entrenamientos/archivo' : '/app'}
          className="w-10 h-10 rounded-full bg-[var(--bg-surface)] border border-[var(--border-subtle)] flex items-center justify-center text-[var(--text-main)] hover:bg-[var(--border-subtle)] transition-colors"
        >
          <SafeIcon name="ArrowLeft" className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-[var(--text-main)] uppercase tracking-tight italic">
            Editar reel
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            Texto y encuadre. Los tiempos del clip no se pueden cambiar después de publicar.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-xl border border-red-400/40 bg-red-500/10 text-red-300 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-[var(--text-secondary)] font-bold uppercase tracking-widest">
          Cargando reel…
        </div>
      ) : !reel ? null : (
        <form onSubmit={handleSave} className="space-y-8">
          <section className="p-6 rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] space-y-4">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-secondary)]">
              Texto del reel
            </p>
            <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-2">
              Título / Caption
            </label>
            <input
              type="text"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              disabled={saving}
              className="w-full bg-[var(--bg-base)] border border-[var(--border-subtle)] rounded-xl py-3 px-4 text-sm text-[var(--text-main)] focus:outline-none focus:border-brand-green"
              placeholder="Título visible en el feed"
            />
            {hasClip && (
              <p className="text-xs text-[var(--text-secondary)]">
                Clip: {reel.clipStartSeconds}s – {reel.clipEndSeconds}s (fijo)
              </p>
            )}
          </section>

          <section className="p-6 rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] space-y-4">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-secondary)]">
              Vista previa en feed (9:16)
            </p>
            <div className="mx-auto w-full max-w-[280px] aspect-[9/16] rounded-2xl overflow-hidden border border-[var(--border-subtle)] bg-black">
              {reel.videoUrl ? (
                <BrandVideoPlayer
                  src={reel.videoUrl}
                  poster={reel.videoThumbnail}
                  className="w-full h-full"
                  videoClassName="w-full h-full"
                  objectFit={previewDisplay.objectFit}
                  objectPosition={previewDisplay.objectPosition}
                  loop
                  muted
                  showCenterPlay={false}
                  showPlayPauseControl={false}
                  tapAction="togglePlay"
                  clipStartSeconds={reel.clipStartSeconds}
                  clipEndSeconds={reel.clipEndSeconds}
                />
              ) : (
                <div
                  className="w-full h-full bg-cover bg-center"
                  style={{ backgroundImage: `url(${reel.videoThumbnail})` }}
                />
              )}
            </div>
          </section>

          {isVertical ? (
            <section className="p-6 rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-surface)]">
              <p className="text-sm text-[var(--text-main)]">
                Este reel ya es vertical. Se muestra a pantalla completa sin recorte adicional.
              </p>
            </section>
          ) : (
            <section className="p-6 rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-surface)]">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-4">
                Encuadre
              </p>
              <ReelCropSettings
                videoSrc={reel.videoUrl}
                aspectRatio={aspectRatio ?? reel.sourceAspectRatio ?? 1.7778}
                value={cropSettings}
                onChange={setCropSettings}
                disabled={saving}
              />
            </section>
          )}

          <button
            type="submit"
            disabled={saving}
            className="w-full md:w-auto flex items-center justify-center gap-3 gfi-gradient-bg text-brand-navy px-10 py-4 rounded-full font-black text-xs uppercase tracking-widest transition-all hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100 shadow-xl"
          >
            {saving ? (
              <>
                <SafeIcon name="Loader" className="w-5 h-5 animate-spin" />
                Guardando…
              </>
            ) : (
              <>
                <SafeIcon name="Check" className="w-5 h-5" />
                Guardar cambios
              </>
            )}
          </button>

          <ReelLifecycleActions
            reel={reel}
            canEdit={canEdit}
            isAdmin={isAdmin}
            onReelUpdated={handleReelUpdated}
          />
        </form>
      )}
    </div>
  );
}
